/** Fake identities for the App Guide — stable per entity id, no real PII. */

export const TUTORIAL_FAKE_DOMAINS = {
  workspace: "summitlane.demo",
  prospect: "demo-prospect.example",
} as const;

/** Legacy production labels replaced during the App Guide. */
export const TUTORIAL_LEGACY_CLIENT = {
  name: "Access Marketing Company",
  domain: "accessmarketingcompany.com",
  acronym: "AMC",
} as const;

export type TutorialFakePersona = {
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  linkedinUrl: string;
};

function persona(
  firstName: string,
  lastName: string,
  emailLocal: string,
  linkedinSlug: string,
  domain: string = TUTORIAL_FAKE_DOMAINS.workspace,
): TutorialFakePersona {
  return {
    firstName,
    lastName,
    fullName: `${firstName} ${lastName}`,
    email: `${emailLocal}@${domain}`,
    linkedinUrl: `https://linkedin.com/in/${linkedinSlug}`,
  };
}

/** Hero hotlist contact (Cellhire). */
export const TUTORIAL_HERO_CONTACT_PERSONA = persona(
  "Alex",
  "Carter",
  "alex.carter",
  "demo-alex-carter",
  TUTORIAL_FAKE_DOMAINS.prospect,
);

/** Zayo contacts shown on the account workspace tour. */
export const TUTORIAL_ZAYO_CONTACT_PERSONAS: Record<number, TutorialFakePersona> = {
  73334: persona("Jordan", "Lee", "jordan.lee", "demo-jordan-lee", TUTORIAL_FAKE_DOMAINS.prospect),
  73332: persona("Taylor", "Morgan", "taylor.morgan", "demo-taylor-morgan", TUTORIAL_FAKE_DOMAINS.prospect),
  73333: persona("Casey", "Brooks", "casey.brooks", "demo-casey-brooks", TUTORIAL_FAKE_DOMAINS.prospect),
};

/** Workspace users referenced often in fixtures (others are generated deterministically). */
export const TUTORIAL_KNOWN_USER_PERSONAS: Record<string, TutorialFakePersona> = {
  "05c77a59-dfd5-4cd7-8058-5feda4eed142": persona("Morgan", "Hayes", "morgan.hayes", "demo-morgan-hayes"),
  "7f0c1aa6-8924-42dd-95e8-12f10775a042": persona("Riley", "Chen", "riley.chen", "demo-riley-chen"),
  "53b47901-fff5-4563-bc8a-13e34f4c2ee4": persona("Sam", "Rivera", "sam.rivera", "demo-sam-rivera"),
};

export const TUTORIAL_KNOWN_CONTACT_PERSONAS: Record<number, TutorialFakePersona> = {
  51662: TUTORIAL_HERO_CONTACT_PERSONA,
  ...TUTORIAL_ZAYO_CONTACT_PERSONAS,
};

const GENERATED_USER_POOL: TutorialFakePersona[] = [
  persona("Avery", "Blake", "avery.blake", "demo-avery-blake"),
  persona("Blake", "Jordan", "blake.jordan", "demo-blake-jordan"),
  persona("Cameron", "Ellis", "cameron.ellis", "demo-cameron-ellis"),
  persona("Dakota", "Reed", "dakota.reed", "demo-dakota-reed"),
  persona("Elliot", "Shaw", "elliot.shaw", "demo-elliot-shaw"),
  persona("Finley", "Grant", "finley.grant", "demo-finley-grant"),
  persona("Harper", "Quinn", "harper.quinn", "demo-harper-quinn"),
  persona("Jamie", "Porter", "jamie.porter", "demo-jamie-porter"),
  persona("Kendall", "Hayes", "kendall.hayes", "demo-kendall-hayes"),
  persona("Logan", "Pierce", "logan.pierce", "demo-logan-pierce"),
  persona("Parker", "Wells", "parker.wells", "demo-parker-wells"),
  persona("Quinn", "Sutton", "quinn.sutton", "demo-quinn-sutton"),
];

const GENERATED_CONTACT_POOL: TutorialFakePersona[] = [
  persona("Alex", "Carter", "alex.carter", "demo-alex-carter", TUTORIAL_FAKE_DOMAINS.prospect),
  persona("Jordan", "Lee", "jordan.lee", "demo-jordan-lee", TUTORIAL_FAKE_DOMAINS.prospect),
  persona("Taylor", "Morgan", "taylor.morgan", "demo-taylor-morgan", TUTORIAL_FAKE_DOMAINS.prospect),
  persona("Casey", "Brooks", "casey.brooks", "demo-casey-brooks", TUTORIAL_FAKE_DOMAINS.prospect),
  persona("Riley", "Chen", "riley.chen", "demo-riley-chen", TUTORIAL_FAKE_DOMAINS.prospect),
  persona("Morgan", "Hayes", "morgan.hayes", "demo-morgan-hayes", TUTORIAL_FAKE_DOMAINS.prospect),
  persona("Drew", "Foster", "drew.foster", "demo-drew-foster", TUTORIAL_FAKE_DOMAINS.prospect),
  persona("Skyler", "Monroe", "skyler.monroe", "demo-skyler-monroe", TUTORIAL_FAKE_DOMAINS.prospect),
  persona("Reese", "Dalton", "reese.dalton", "demo-reese-dalton", TUTORIAL_FAKE_DOMAINS.prospect),
  persona("Sage", "Holland", "sage.holland", "demo-sage-holland", TUTORIAL_FAKE_DOMAINS.prospect),
];

function hashKey(value: string | number): number {
  const text = String(value);
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
  }
  return hash;
}

export function getTutorialUserPersona(userId: string): TutorialFakePersona {
  const known = TUTORIAL_KNOWN_USER_PERSONAS[userId];
  if (known) return known;
  return GENERATED_USER_POOL[hashKey(userId) % GENERATED_USER_POOL.length];
}

export function getTutorialContactPersona(contactId: number): TutorialFakePersona {
  const known = TUTORIAL_KNOWN_CONTACT_PERSONAS[contactId];
  if (known) return known;
  return GENERATED_CONTACT_POOL[hashKey(contactId) % GENERATED_CONTACT_POOL.length];
}

export function fakeTutorialPhone(seed: string | number): string {
  const digits = String(1000 + (hashKey(seed) % 9000));
  return `+1-555-01${digits}`;
}

/** Longest-first text replacements for message bodies and summaries. */
export const TUTORIAL_REAL_NAME_REPLACEMENTS: ReadonlyArray<readonly [string, string]> = [
  ["Duncan Griffiths", TUTORIAL_HERO_CONTACT_PERSONA.fullName],
  ["Missy Altergott", TUTORIAL_ZAYO_CONTACT_PERSONAS[73334].fullName],
  ["Brian Daniels", TUTORIAL_ZAYO_CONTACT_PERSONAS[73332].fullName],
  ["Chris Ranalli", TUTORIAL_ZAYO_CONTACT_PERSONAS[73333].fullName],
  ["Justin Marsh", TUTORIAL_KNOWN_USER_PERSONAS["05c77a59-dfd5-4cd7-8058-5feda4eed142"].fullName],
  ["Bailey Ingolia", TUTORIAL_KNOWN_USER_PERSONAS["7f0c1aa6-8924-42dd-95e8-12f10775a042"].fullName],
  ["Andrew Davis", TUTORIAL_KNOWN_USER_PERSONAS["53b47901-fff5-4563-bc8a-13e34f4c2ee4"].fullName],
  ["Duncan", TUTORIAL_HERO_CONTACT_PERSONA.firstName],
  ["Missy", TUTORIAL_ZAYO_CONTACT_PERSONAS[73334].firstName],
];
